import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Lock, GitBranch, Brain, Users, CheckCircle, Shield, FileText, TrendingUp, DollarSign } from "lucide-react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";

// System Layer Data - 5 Layers
const systemLayers = [
  {
    id: 1,
    icon: Lock,
    title: "Authority Intake Engine",
    subtitle: "Intake & Qualification",
    purpose: "Control who enters the system, when, and why—without founder involvement.",
    whatItDoes: "The Authority Intake Engine filters inbound demand through structured applications, qualification logic, and decision rules. It replaces subjective founder judgment with objective intake criteria.",
    whyItMatters: [
      "Eliminates low-quality demand",
      "Standardizes entry requirements",
      "Signals authority through selectivity",
      "Increases buyer confidence in pipeline quality",
    ],
    operationalOutput: "Approved, Hold, or Declined outcomes—fully documented and auditable.",
  },
  {
    id: 2,
    icon: GitBranch,
    title: "Decision Control Layer",
    subtitle: "Codification",
    purpose: "Centralize approval logic and eliminate discretionary decision-making.",
    whatItDoes: "This layer governs how decisions are made, recorded, and triggered across engagements. Each decision outcome activates predefined workflows, communications, and internal actions.",
    whyItMatters: [
      "Removes founder bottlenecks",
      "Creates consistent governance",
      "Reduces operational risk",
      "Enables delegation without authority loss",
    ],
    operationalOutput: "A documented decision trail that is buyer-readable and operator-executable.",
  },
  {
    id: 3,
    icon: Brain,
    title: "Follow-Up Intelligence Engine",
    subtitle: "Certification",
    purpose: "Maintain authority-driven relationship progression without manual oversight.",
    whatItDoes: "Manages timing, messaging, and escalation logic for approved and hold-status relationships. Follow-up is governed by system logic, not memory or emotion.",
    whyItMatters: [
      "Preserves long-cycle opportunities",
      "Prevents authority erosion through over-communication",
      "Ensures consistent engagement cadence",
    ],
    operationalOutput: "System-triggered follow-up sequences tied to decision status and readiness.",
  },
  {
    id: 4,
    icon: Users,
    title: "Operator Handoff Architecture",
    subtitle: "Governance",
    purpose: "Transfer authority from founder to system to operator.",
    whatItDoes: "This layer enables certified operators to execute authority-bearing actions using documented systems, scripts, and governance rules.",
    whyItMatters: [
      "Enables founder step-back",
      "Supports licensing and certification",
      "Creates scalable delivery capacity",
      "Increases exit optionality",
    ],
    operationalOutput: "Operator-led execution with founder-level authority integrity.",
  },
  {
    id: 5,
    icon: DollarSign,
    title: "Licensing & Renewal Engine",
    subtitle: "Monetization",
    purpose: "Scale revenue independent of founder involvement.",
    whatItDoes: "This layer manages certification fees, annual licensing, and renewal compliance. Revenue scales with operator count, not founder time.",
    whyItMatters: [
      "Creates recurring revenue streams",
      "Enables clean multiple expansion",
      "Supports operator-driven growth",
      "Reduces key-person revenue dependency",
    ],
    operationalOutput: "High-margin licensing revenue with low variable cost base.",
  },
];

// Diagram Components
const AuthorityFlowDiagram = () => (
  <div className="bg-[#0a0a0a] border border-white/10 p-6 md:p-8" data-testid="authority-flow-diagram">
    <p className="text-xs font-mono text-primary mb-2 uppercase tracking-wider">OnPoint Authority Infrastructure Stack</p>
    <p className="text-xs text-muted-foreground mb-6">Diagram 1: Authority Flow</p>
    
    {/* Visual Flow Diagram */}
    <div className="relative">
      {/* Flow Steps */}
      <div className="flex flex-col md:flex-row items-stretch gap-2 md:gap-0">
        {[
          { label: "Intake & Qualification", sublabel: "Authority Application Engine" },
          { label: "Codification", sublabel: "Authority Architecture Blueprint" },
          { label: "Certification", sublabel: "Operator Certification Engine" },
          { label: "Governance", sublabel: "Internal Review & Approval" },
          { label: "Monetization", sublabel: "Licensing & Renewal Engine" },
        ].map((step, index) => (
          <div key={index} className="flex-1 flex items-center">
            <div className="flex-1 bg-card/5 border border-white/5 p-4 text-center relative">
              <p className="text-xs font-mono text-primary mb-1">Layer {index + 1}</p>
              <p className="text-sm font-medium text-foreground mb-1">{step.label}</p>
              <p className="text-xs text-muted-foreground">{step.sublabel}</p>
            </div>
            {index < 4 && (
              <div className="hidden md:flex items-center justify-center w-6">
                <ArrowRight className="h-4 w-4 text-primary" />
              </div>
            )}
            {index < 4 && (
              <div className="md:hidden flex items-center justify-center h-6">
                <ArrowRight className="h-4 w-4 text-primary rotate-90" />
              </div>
            )}
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground text-center mt-4 italic">
        Founder presence not required for system continuity.
      </p>
    </div>
  </div>
);

const DecisionTreeDiagram = () => (
  <div className="bg-[#0a0a0a] border border-white/10 p-6 md:p-8" data-testid="decision-tree-diagram">
    <p className="text-xs font-mono text-primary mb-6 uppercase tracking-wider">Diagram 2: Decision Tree</p>
    
    {/* Decision Tree Visual */}
    <div className="space-y-4">
      {/* Root */}
      <div className="flex justify-center">
        <div className="bg-primary/10 border border-primary/30 px-6 py-3 text-center">
          <p className="text-sm font-medium text-foreground">Application Submitted</p>
          <p className="text-xs text-primary mt-1">Decision Authority</p>
        </div>
      </div>
      
      {/* Connector */}
      <div className="flex justify-center">
        <div className="w-px h-8 bg-white/20" />
      </div>
      
      {/* Branches */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-green-500/10 border border-green-500/30 p-4 text-center">
          <CheckCircle className="h-5 w-5 text-green-400 mx-auto mb-2" />
          <p className="text-sm font-medium text-green-400">Approved</p>
          <p className="text-xs text-muted-foreground mt-1">Immediate Engagement</p>
          <p className="text-xs text-green-400/70 mt-2">Risk Mitigation ✓</p>
        </div>
        <div className="bg-yellow-500/10 border border-yellow-500/30 p-4 text-center">
          <Shield className="h-5 w-5 text-yellow-400 mx-auto mb-2" />
          <p className="text-sm font-medium text-yellow-400">Hold</p>
          <p className="text-xs text-muted-foreground mt-1">Timed Nurture Logic</p>
          <p className="text-xs text-yellow-400/70 mt-2">System-Enforced Outcome</p>
        </div>
        <div className="bg-red-500/10 border border-red-500/30 p-4 text-center">
          <FileText className="h-5 w-5 text-red-400 mx-auto mb-2" />
          <p className="text-sm font-medium text-red-400">Declined</p>
          <p className="text-xs text-muted-foreground mt-1">Authority Close</p>
          <p className="text-xs text-red-400/70 mt-2">System-Enforced Outcome</p>
        </div>
      </div>
    </div>
  </div>
);

const FounderTransferDiagram = () => (
  <div className="bg-[#0a0a0a] border border-white/10 p-6 md:p-8" data-testid="founder-transfer-diagram">
    <p className="text-xs font-mono text-primary mb-6 uppercase tracking-wider">Diagram 3: Founder-to-Operator Transfer</p>
    
    {/* Transfer Flow */}
    <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8">
      {/* Founder Authority */}
      <div className="w-full md:w-auto">
        <div className="bg-primary/20 border-2 border-primary/50 p-6 text-center min-w-[180px]">
          <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-3">
            <Users className="h-6 w-6 text-primary" />
          </div>
          <p className="text-sm font-medium text-foreground">Founder Authority</p>
          <p className="text-xs text-primary mt-2">Authority Source</p>
        </div>
      </div>

      {/* Arrow */}
      <div className="hidden md:block">
        <ArrowRight className="h-6 w-6 text-white/30" />
      </div>
      <div className="md:hidden">
        <ArrowRight className="h-6 w-6 text-white/30 rotate-90" />
      </div>

      {/* System Governance */}
      <div className="w-full md:w-auto">
        <div className="bg-card/10 border-2 border-white/20 p-6 text-center min-w-[180px]">
          <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-3">
            <Shield className="h-6 w-6 text-foreground" />
          </div>
          <p className="text-sm font-medium text-foreground">System Governance</p>
          <p className="text-xs text-muted-foreground mt-2">Control Layer</p>
        </div>
      </div>

      {/* Arrow */}
      <div className="hidden md:block">
        <ArrowRight className="h-6 w-6 text-white/30" />
      </div>
      <div className="md:hidden">
        <ArrowRight className="h-6 w-6 text-white/30 rotate-90" />
      </div>

      {/* Certified Operator */}
      <div className="w-full md:w-auto">
        <div className="bg-green-500/10 border-2 border-green-500/30 p-6 text-center min-w-[180px]">
          <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
            <TrendingUp className="h-6 w-6 text-green-400" />
          </div>
          <p className="text-sm font-medium text-foreground">Certified Operator</p>
          <p className="text-xs text-green-400 mt-2">Scalable Execution</p>
        </div>
      </div>
    </div>
  </div>
);

const SystemsPage = () => {
  return (
    <div className="min-h-screen bg-[#080808]" data-testid="systems-page">
      <SEOHead 
        title="Enterprise Authority Systems & Operator Licensing"
        description="Authority systems designed for scale, governance, and independence from founder presence."
        url="https://onpointauthority.com/systems"
      />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 px-6 md:px-12" data-testid="systems-hero">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-6">
              Product & IP
            </p>
            <h1 className="font-serif text-5xl md:text-7xl font-light tracking-tight leading-[0.95] text-foreground mb-8">
              Authority Systems,<br />
              <span className="text-primary">Not Marketing Assets</span>
            </h1>
            <div className="space-y-6 text-lg font-light leading-relaxed text-muted-foreground max-w-3xl">
              <p>
                Every system we build is designed to survive founder absence. Authority is embedded 
                through process, certification, governance, and enforcement—not personality.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4">
                {[
                  "Authority Architecture Blueprint",
                  "Operator Certification Engine",
                  "Internal Review & Approval System",
                  "Brand & IP Governance Layer",
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                    <span className="text-foreground">{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-primary pt-4">
                A business that scales without dilution and survives diligence without explanation.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* System Architecture Layers */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="architecture-layers">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              System Architecture Layers
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { title: "Institutional Authority", label: "Authority design" },
              { title: "The Certification Gap", label: "Operator certification" },
              { title: "Enforce or Erode", label: "Enforcement mechanics" },
              { title: "Governance Beyond Founders", label: "Post-founder governance" },
            ].map((chapter, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-card/5 border border-white/10 p-6"
              >
                <h3 className="font-serif text-xl font-light text-foreground mb-2">{chapter.title}</h3>
                <p className="text-sm text-muted-foreground">{chapter.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* System Layers */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="system-layers">
        <div className="max-w-7xl mx-auto">
          <div className="space-y-16 md:space-y-24">
            {systemLayers.map((layer, index) => (
              <motion.div
                key={layer.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="border border-white/5"
                data-testid={`system-layer-${index}`}
              >
                {/* Layer Header */}
                <div className="p-6 md:p-10 border-b border-white/5 bg-card/5">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <layer.icon className="h-7 w-7 text-primary" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="text-xs font-mono text-muted-foreground mb-1">
                        LAYER {layer.id} — {layer.subtitle.toUpperCase()}
                      </p>
                      <h2 className="font-serif text-3xl md:text-4xl font-normal text-foreground">
                        {layer.title}
                      </h2>
                    </div>
                  </div>
                </div>

                {/* Layer Content */}
                <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-white/5">
                  {/* Purpose & What It Does */}
                  <div className="p-6 md:p-10 space-y-8">
                    <div>
                      <p className="text-xs font-medium tracking-[0.2em] uppercase text-primary mb-4">
                        Purpose
                      </p>
                      <p className="text-lg text-foreground font-light">
                        {layer.purpose}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
                        What It Does
                      </p>
                      <p className="text-base text-muted-foreground leading-relaxed">
                        {layer.whatItDoes}
                      </p>
                    </div>
                  </div>

                  {/* Why It Matters & Output */}
                  <div className="p-6 md:p-10 space-y-8">
                    <div>
                      <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
                        Why It Matters
                      </p>
                      <ul className="space-y-2">
                        {layer.whyItMatters.map((item, i) => (
                          <li key={i} className="flex items-start gap-3 text-sm text-muted-foreground">
                            <CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-primary/5 border border-primary/20 p-4">
                      <p className="text-xs font-medium tracking-[0.2em] uppercase text-primary mb-2">
                        Operational Output
                      </p>
                      <p className="text-sm text-foreground">
                        {layer.operationalOutput}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* System Diagrams */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#050505]" data-testid="system-diagrams">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Visual Architecture
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-foreground">
              System<br />
              <span className="text-primary">Diagrams</span>
            </h2>
          </motion.div>

          <div className="space-y-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <AuthorityFlowDiagram />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <DecisionTreeDiagram />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <FounderTransferDiagram />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Professional Workflow Diagrams */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="workflow-diagrams">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12 text-center"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Process Documentation
            </p>
            <h2 className="font-serif text-4xl font-light text-foreground">
              Authority <span className="text-primary">Workflows</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Authority Layers */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-card/5 border border-white/10 p-6"
            >
              <p className="text-xs font-mono text-primary mb-4">Authority Infrastructure</p>
              <ul className="space-y-3">
                {["Content Layer", "Certification Layer", "Licensing Layer", "Enforcement Layer"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-foreground">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* License Lifecycle */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-card/5 border border-white/10 p-6"
            >
              <p className="text-xs font-mono text-primary mb-4">License Lifecycle</p>
              <ul className="space-y-3">
                {["Issued", "Monitoring", "Audit", "Review", "Outcome"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-foreground">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Certification Flow */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-card/5 border border-white/10 p-6"
            >
              <p className="text-xs font-mono text-primary mb-4">Certification Process</p>
              <ul className="space-y-3">
                {["Application", "Module Completion", "Exam", "Review", "License Eligible"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-foreground">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Enforcement Flow */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-card/5 border border-white/10 p-6"
            >
              <p className="text-xs font-mono text-red-400 mb-4">Enforcement Process</p>
              <ul className="space-y-3">
                {["Violation Detection", "Evidence Collection", "Review Board", "Remediation/Revocation", "Log Lock"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-foreground">
                    <div className="w-1.5 h-1.5 bg-red-400 rounded-full" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* System Ownership & IP */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="system-ownership">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Intellectual Property
            </p>
            <h2 className="font-serif text-3xl md:text-4xl font-normal tracking-tight text-foreground mb-6">
              System Ownership<br />
              <span className="text-primary">& IP</span>
            </h2>
            <p className="text-lg font-light leading-relaxed text-muted-foreground mb-10">
              All system layers are proprietary, documented, and owned by OnPoint Authority Systems, Inc. 
              The infrastructure is designed for transferability under licensing, acquisition, or internal 
              succession scenarios.
            </p>
            <Link to="/authority-review">
              <Button
                className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.15em] text-xs font-bold px-10 py-6 rounded-none gold-glow"
                data-testid="systems-cta"
              >
                Request Authority Review
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SystemsPage;
